import AnimatedSection from "@/utils/AnimatedSection";

const IconComponents = {
  bone: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8" stroke="currentColor" strokeWidth="1.5">
      <path d="M4.5 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 2.5 2.5c0 .5-.15 1-.4 1.4L9.5 11l2.9-2.9c-.25-.4-.4-.9-.4-1.4a2.5 2.5 0 1 1 5 0c0 .5-.15 1-.4 1.4l2.9 2.9 2.9-2.9c-.25-.4-.4-.9-.4-1.4a2.5 2.5 0 1 1 0 5c-.5 0-1-.15-1.4-.4L17.5 14.5l2.9 2.9c.4-.25.9-.4 1.4-.4a2.5 2.5 0 1 1 0 5c-.5 0-1-.15-1.4-.4l-2.9-2.9-2.9 2.9c.25.4.4.9.4 1.4a2.5 2.5 0 1 1-5 0c0-.5.15-1 .4-1.4l-2.9-2.9-2.9 2.9c.25.4.4.9.4 1.4a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 2.5-2.5c.5 0 1 .15 1.4.4l2.9-2.9-2.9-2.9c-.4.25-.9.4-1.4.4a2.5 2.5 0 0 1 0-5z" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  chart: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 20h18M6 20V10m5 10V4m5 16v-8" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="6" cy="10" r="1.5" fill="currentColor"/>
      <circle cx="11" cy="4" r="1.5" fill="currentColor"/>
      <circle cx="16" cy="12" r="1.5" fill="currentColor"/>
    </svg>
  ),
  nutrition: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 2C8.5 2 6 4 6 7c0 2 1 3.5 2.5 4.5L8 22h8l-.5-10.5C17 10.5 18 9 18 7c0-3-2.5-5-6-5z" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 2v6m-3 0h6" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M10 14h4" strokeLinecap="round"/>
    </svg>
  ),
  brain: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 4.5a4.5 4.5 0 0 0-4.5 4.5c0 1.5.7 2.8 1.8 3.6-.3.8-.8 1.6-1.5 2.2a7 7 0 0 0 4.2 1.7 7 7 0 0 0 4.2-1.7c-.7-.6-1.2-1.4-1.5-2.2a4.5 4.5 0 0 0 1.8-3.6 4.5 4.5 0 0 0-4.5-4.5z" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M12 16.5V22M9 19h6" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M7.5 9a2.5 2.5 0 1 1 0-5M16.5 9a2.5 2.5 0 1 0 0-5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  ),
  tooth: () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-8 h-8" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 2c-2.5 0-4 1-5 2s-2 3-2 5c0 3 1 5 2 8s1.5 5 2 5c.5 0 1-1 1.5-2.5.5 1.5 1 2.5 1.5 2.5s1-2 2-5 2-5 2-8c0-2-1-4-2-5s-2.5-2-5-2z" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9.5 8h5" strokeLinecap="round"/>
    </svg>
  )
};

const dimensions = [
  { icon: "bone", tag: "Growth Monitoring", title: "Skeletal Growth Assessment", description: "Understand your child's skeletal development and maturity with precision assessment — helping detect growth concerns early before they become complex issues." },
  { icon: "chart", tag: "Predictive Insights", title: "Growth & Development Tracking", description: "Track your child's height, weight, and developmental trajectory against real-time global benchmarks. Spot deviations early and get personalized guidance." },
  { icon: "nutrition", tag: "Personalized Nutrition", title: "Nutrition & Wellness Planning", description: "Receive customized dietary recommendations for your child based on age, growth stage, and health data — backed by globally recognized nutritional standards." },
  { icon: "brain", tag: "Cognitive Assessment", title: "Cognitive & Mental Development", description: "Evaluate your child's cognitive abilities and mental wellness through engaging, child-friendly assessments. Understand strengths and identify areas needing early support." },
  { icon: "tooth", tag: "Early Detection", title: "Dental Health Screening", description: "Get instant analysis of your child's dental health through a simple, non-invasive scan — identifying early signs of decay, misalignment, or developmental concerns." }
];

const DimensionCard = ({ dim, index, theme }) => (
  <div 
    className="group relative rounded-2xl p-8 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl border-l-4 border-transparent hover:border-[#4ECDC4] bg-[#1e2642] border border-white/10"
    data-testid={`dimension-card-${index}`}
  >
    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6 bg-[#A855F7]/20 text-[#A855F7]">
      {IconComponents[dim.icon]()}
    </div>
    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3 bg-[#4ECDC4]/20 text-[#4ECDC4]">
      {dim.tag}
    </span>
    <h3 className="font-display text-xl mb-3 text-white">{dim.title}</h3>
    <p className="text-sm leading-relaxed text-white/60">{dim.description}</p>
  </div>
);

const HealthDimensions = ({ theme }) => {
  return (
    <section id="health-dimensions-section" className="py-24 relative overflow-hidden bg-mesh" data-testid="health-dimensions-section">
      <div className="orb orb-teal w-[400px] h-[400px] -top-40 -left-40" />
      <div className="orb orb-purple w-[350px] h-[350px] bottom-20 -right-20" />
      
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <AnimatedSection>
          <div className="text-center mb-16">
            <span className="badge-glass inline-flex px-4 py-1.5 rounded-full text-xs font-semibold text-[#A855F7] mb-5 uppercase tracking-wider">
              The 5 Dimensions
            </span>
            <h2 className="font-display text-4xl md:text-5xl text-primary mb-4">
              A Complete Picture of Your Child's Health
            </h2>
          </div>
        </AnimatedSection>

        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {dimensions.slice(0, 4).map((dim, i) => (
            <AnimatedSection key={i} delay={i * 0.1}>
              <DimensionCard dim={dim} index={i} theme={theme} />
            </AnimatedSection>
          ))}
        </div>

        <AnimatedSection delay={0.4}>
          <div className="max-w-lg mx-auto">
            <DimensionCard dim={dimensions[4]} index={4} theme={theme} />
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default HealthDimensions;
